/**
 * Environment Configuration & Validation
 * 
 * Validates required environment variables at startup
 * and provides type-safe access to configuration
 */

interface Config {
  apiUrl: string;
  environment: string;
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Validate that all required environment variables are set
 * Throws an error if any are missing
 */
export const validateEnv = (): void => {
  const requiredVars = [
    'REACT_APP_API_URL'
  ];

  const missing = requiredVars.filter(varName => {
    const value = process.env[varName];
    return !value || value.trim() === '';
  });

  if (missing.length > 0) {
    const errorMessage = [
      '❌ Missing required environment variables:',
      '',
      missing.map(v => `  • ${v}`).join('\n'),
      '',
      '📝 To fix this:',
      '  1. Create a .env file in the project root',
      '  2. Copy contents from .env.example',
      '  3. Fill in the values',
      '',
      'Example .env file:',
      '  REACT_APP_API_URL=https://your-strapi-url.com/api',
      ''
    ].join('\n');

    console.error(errorMessage);
    
    throw new Error(
      `Missing required environment variables: ${missing.join(', ')}\n` +
      `Please create a .env file based on .env.example`
    );
  }
};

/**
 * Type-safe configuration object
 */
export const config: Config = {
  apiUrl: process.env.REACT_APP_API_URL || '',
  environment: process.env.NODE_ENV || 'development',
  isDevelopment: process.env.NODE_ENV === 'development',
  isProduction: process.env.NODE_ENV === 'production',
};

/**
 * Usage:
 * 
 * // In index.tsx (before rendering):
 * import { validateEnv } from './config/env';
 * validateEnv(); // Throws error if env vars missing
 * 
 * // In components/services:
 * import { config } from './config/env';
 * const apiUrl = config.apiUrl;
 * 
 * if (config.isDevelopment) {
 *   console.log('Running in development mode');
 * }
 */

export default config;
